import React, { Children } from 'react'
import { List, ListItem, H3, Separator, Divider, FlowGrid, Paragraph, useContainerWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { baseUrl } from '@fs/zion-config'
import {
  moreToExploreBrowseSvg,
  moreToExploreExpoHallSvg,
  moreToExploreFamilyTreeSvg,
  moreToExploreFamousRelativesSvg,
  moreToExploreMemoriesSvg,
  moreToExploreMiniEventsSvg,
  moreToExplorePlaylistsSvg,
  moreToExploreRegionalPageSvg,
  moreToExploreRelativesAtRootstechSvg,
  moreToExploreScheduleSvg,
  moreToExploreSearchSvg,
  moreToExploreSpeakersSvg,
  moreToExploreKeynotesSvg,
  moreToExplorePrepareSvg,
  moreToExplorePromotionsSvg,
  moreToExploreRegistrationSvg,
} from '../../../images'
import { FLAGS } from '../../../config'
import { useFlagsContext } from '../../../providers'
import ResponsiveLayoutBand from '../../../lib/ResponsiveLayoutBand'
import useShowExpoHall from '../../../hooks/useShowExpoHall'

const MoreToExploreCard = ({ subheading, items }) => {
  const atWidth = useContainerWidth()
  const dense = atWidth({ default: true, md: false })
  return (
    <div>
      <Paragraph size="sm">{subheading}</Paragraph>
      <Divider />
      <List>{Children.toArray(items.map((item) => <ListItem {...item} emphasized dense={dense} />))}</List>
      {dense && <Separator size="xs" />}
    </div>
  )
}

const MoreToExplore = () => {
  const atWidth = useContainerWidth()
  const { t } = useTranslation()
  const { [FLAGS.SITE_EXPERIENCE]: siteExperienceFlag } = useFlagsContext()
  const { treatment } = siteExperienceFlag
  const showExpoHall = useShowExpoHall()

  const moreToExploreHeading = t('home.more-to-explore.heading', 'More to Explore')

  const scheduleLink = {
    startElement: <ListItem.Image src={moreToExploreScheduleSvg} alt="" />,
    primaryText:
      treatment !== 'registration'
        ? t('navigation.class-schedule.label', 'Class Schedule')
        : t('home.more-to-explore.item.primary-text-class-preview', 'Class Preview'),
    to: '/schedule',
    linkName: 'class_preview',
    'data-testid': 'class-preview',
  }
  const speakersLink = {
    startElement: <ListItem.Image src={moreToExploreSpeakersSvg} alt="" />,
    primaryText: t('home.more-to-explore.item.primary-text-see-our-speakers', 'See Our Speakers'),
    to: '/speakers',
    linkName: 'see_our_speakers',
    'data-testid': 'see-our-speakers',
  }
  const ldsSessionsLink = {
    startElement: <ListItem.Image src={moreToExploreMiniEventsSvg} alt="" />,
    primaryText: t('home.more-to-explore.item.latter-day-saint-sessions', 'Latter-day Saint Sessions'),
    to: '/topics/latter-day-saint-sessions',
    linkName: 'latter-day_saint_sessions',
    'data-testid': 'latter-day-saint-sessions',
  }
  const promotionsLink = {
    startElement: <ListItem.Image src={moreToExplorePromotionsSvg} alt="" />,
    primaryText: t('expo-hall.promotions.heading', 'Promotions for RootsTech'),
    to: '/expohall/promotions',
    linkName: 'Promotions for RootsTech',
    'data-testid': 'promotions-for-rootstech',
  }
  const expoHallLink = {
    startElement: <ListItem.Image src={moreToExploreExpoHallSvg} alt="" />,
    primaryText: t('home.more-to-explore.item.primary-text-expo-hall', 'Expo Hall'),
    to: '/expohall',
    linkName: 'expo_hall',
    'data-testid': 'expo-hall',
  }
  const registerLink = {
    startElement: <ListItem.Image src={moreToExploreRegistrationSvg} alt="" />,
    primaryText: t('home.more-to-explore.item.register', 'Register for RootsTech'),
    to: '/registration',
    linkName: 'register',
    'data-testid': 'register',
  }
  const keynotesLink = {
    startElement: <ListItem.Image src={moreToExploreKeynotesSvg} alt="" />,
    primaryText: t('home.more-to-explore.item.keynotes', 'Meet Our Keynotes'),
    to: '/topics/keynotes',
    linkName: 'keynotes',
    'data-testid': 'keynotes',
  }
  const prepareLink = {
    startElement: <ListItem.Image src={moreToExplorePrepareSvg} alt="" />,
    primaryText: t('home.more-to-explore.item.preparing', 'Preparing for RootsTech'),
    to: '/registration/prepare',
    linkName: 'prepare',
    'data-testid': 'prepare',
  }

  const moreToExploreData = [
    {
      subheading: t('home.more-to-explore.subheading-event', 'RootsTech Event'),
      items: [
        scheduleLink,
        ...(treatment === 'countdown' ? [keynotesLink] : []),
        ...(treatment === 'conference' ? [promotionsLink] : []),
        speakersLink,
        ...(treatment === 'registration' ? [registerLink] : []),
        ...(showExpoHall ? [expoHallLink] : []),
        ...(treatment === 'countdown' ? [prepareLink] : []),
        ldsSessionsLink,
      ],
    },
    {
      subheading: t('home.more-to-explore.subheading-content-library', 'Content Library'),
      items: [
        {
          startElement: <ListItem.Image src={moreToExploreSearchSvg} alt="" />,
          primaryText: t('common-ui:search.action', 'Search'),
          to: '/search',
          linkName: 'search',
          'data-testid': 'search',
        },
        {
          startElement: <ListItem.Image src={moreToExplorePlaylistsSvg} alt="" />,
          primaryText: t('home.more-to-explore.item.primary-text-my-playlist', 'My Playlist'),
          to: '/playlists',
          linkName: 'my_playlist',
          'data-testid': 'my-playlist',
        },
        {
          startElement: <ListItem.Image src={moreToExploreBrowseSvg} alt="" />,
          primaryText: t('home.more-to-explore.item.primary-text-browse-sessions', 'Browse Sessions'),
          to: '/library',
          linkName: 'browse_sessions',
          'data-testid': 'browse-sessions',
        },
        {
          startElement: <ListItem.Image src={moreToExploreRegionalPageSvg} alt="" />,
          primaryText: t('home.more-to-explore.item.primary-text-regional-pages', 'Regional Pages'),
          to: '/regions',
          linkName: 'regional_pages',
          'data-testid': 'regional-pages',
        },
      ],
    },
    {
      subheading: t('home.more-to-explore.subheading-connect-with-family-search', 'Connect with FamilySearch'),
      items: [
        {
          startElement: <ListItem.Image src={moreToExploreFamilyTreeSvg} alt="" />,
          primaryText: t('home.more-to-explore.item.primary-text-family-tree', 'Build Your Family Tree'),
          to: `${baseUrl}/tree/guided/connect-to-tree`,
          linkName: 'build_your_family_tree',
          'data-testid': 'build-your-family-tree',
          target: '_blank',
        },
        {
          startElement: <ListItem.Image src={moreToExploreRelativesAtRootstechSvg} alt="" />,
          primaryText: t('home.more-to-explore.item.primary-text-together', 'Together App'),
          to: `${baseUrl}/discovery/together`,
          linkName: 'together_app',
          'data-testid': 'together-app',
          target: '_blank',
        },
        {
          startElement: <ListItem.Image src={moreToExploreFamousRelativesSvg} alt="" />,
          primaryText: t('home.more-to-explore.item.primary-text-famous-relatives', 'Famous Relatives'),
          to: `${baseUrl}/connect/famous`,
          linkName: 'famous_relatives',
          'data-testid': 'famous-relatives',
          target: '_blank',
        },
        {
          startElement: <ListItem.Image src={moreToExploreMemoriesSvg} alt="" />,
          primaryText: t('home.more-to-explore.item.primary-text-surname-discovery', 'Surname Discovery'),
          to: `${baseUrl}/surname`,
          linkName: 'surname_discovery',
          'data-testid': 'surname-discovery',
          target: '_blank',
        },
      ],
    },
  ]

  return (
    <ResponsiveLayoutBand size="xl">
      <H3 centered>{moreToExploreHeading}</H3>
      <Separator />
      <FlowGrid columnCount={atWidth({ default: 1, md: 3 })} gutters={atWidth({ default: 'xs', xs: 'xs', sm: 'sm' })}>
        {Children.toArray(
          moreToExploreData.map((data) => <MoreToExploreCard subheading={data.subheading} items={data.items} />)
        )}
      </FlowGrid>
    </ResponsiveLayoutBand>
  )
}

export default MoreToExplore
